import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { computeReadiness, getCurrentWednesday, getNextWednesday, ReadyRowData, ReadyState, ReadyTarget } from "./readiness";
import { flashSpotlight, SPOTLIGHT_RADII } from "./spotlight";
import { ruleForAccount } from "./lib/regionReset";

// Home page counterpart to the profile page's RosterReadinessTile: the same
// checklist laid out sideways, wearing GuildCommandCenter's shell so an officer
// who sees both bars reads them as one family.
//
// It only ever shows what's actually blocking you. Everything green collapses
// the strip to a single quiet line, with the full list still one click away in
// the "All checks" dropdown, so a sorted player isn't reading a wall of ticks
// every time they load the home page.

interface Week {
  weekStart: string;
  weekEnd: string;
  slots: string;
  confirmedAt: string | null;
  confirmedVia: string | null;
  copiedFromWeek?: string;
}

interface WeeklyAvailability {
  currentWeek: Week | null;
  nextWeek: Week | null;
}

interface OptionGroup {
  id: number;
  key: string;
  options: { id: number }[];
}

/* ---------- icons: same shapes the profile card uses ---------- */

function RowIcon({ state, optional }: { state: ReadyState; optional?: boolean }) {
  if (state === "done") {
    return (
      <svg className={`h-[15px] w-[15px] shrink-0 ${optional ? "text-emerald-700/50 dark:text-emerald-400/50" : "text-emerald-700 dark:text-emerald-400"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="12" cy="12" r="9" />
        <path d="M8.5 12.5l2.5 2.5 4.5-5" />
      </svg>
    );
  }
  if (state === "attention") {
    return (
      <svg className="h-[15px] w-[15px] shrink-0 text-amber-700 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7.5v5M12 16.2v.3" />
      </svg>
    );
  }
  return (
    <svg className={`h-[15px] w-[15px] shrink-0 ${optional ? "text-[var(--c-ink-4)]" : "text-[var(--c-ink-4)]"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeDasharray="3 3" aria-hidden>
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}

const ChecklistIcon = () => (
  <svg className="h-[14px] w-[14px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 6l2 2 3-3M4 14l2 2 3-3M13 7h7M13 15h7" />
  </svg>
);

const ChevronIcon = () => (
  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const SyncIcon = () => (
  <svg className="h-3.5 w-3.5 shrink-0 text-[var(--c-ink-3)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const ArrowIcon = () => (
  <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

/* ---------- navigation handoffs ---------- */

// Tab switch plus a scroll, without RosterPage needing to know we did it.
// flashSpotlight waits for the anchor to mount, scrolls to it and rings it in
// the onboarding tour's amber, so you can see which section you were sent to
// instead of guessing at a wall of page. It gives up quietly if the anchor
// never shows.
function goToTabAndScroll(tab: string, elementId?: string) {
  window.location.hash = tab;
  if (!elementId) return;
  flashSpotlight(`#${elementId}`, { radius: SPOTLIGHT_RADII[elementId] ?? 16 });
}

export default function PlayerReadyBar({
  characters: allCharacters,
  accountAvailability,
  weeklyAvailability,
  setWeeklyAvailability,
  children,
}: {
  characters: any[];
  accountAvailability: boolean[];
  weeklyAvailability: WeeklyAvailability;
  // Loosely typed on purpose: App's two week shapes differ slightly (next week
  // carries copy-forward fields), and a Dispatch of that union won't narrow.
  setWeeklyAvailability: (updater: (prev: any) => any) => void;
  /** Nested below the bar inside the same shell, e.g. the weekly lockout rail. */
  children?: React.ReactNode;
}) {
  // Sleeping characters ask for nothing: readiness is about the awake ones, and
  // a player with every character asleep gets no checklist at all.
  const characters = useMemo(() => (allCharacters || []).filter((c: any) => !c.isSleeping), [allCharacters]);
  const [fieldsByGame, setFieldsByGame] = useState<Record<string, OptionGroup[]>>({});
  const [squad, setSquad] = useState<{ name: string; members: unknown[] } | null>(null);
  const [confirming, setConfirming] = useState<"current" | "next" | null>(null);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Raid interests live in the game's option definitions, and squad membership
  // is its own endpoint. Neither is on the home page already, so fetch them
  // once. Failures leave those rows in their "unknown" state rather than
  // blocking the rest of the checklist.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const gRes = await fetch("/games", { credentials: "include" });
        if (!gRes.ok) return;
        const games = await gRes.json();
        const next: Record<string, OptionGroup[]> = {};
        await Promise.all(
          (games || []).map(async (g: any) => {
            try {
              const fRes = await fetch(`/games/${g.id}/fields`, { credentials: "include" });
              if (fRes.ok) next[g.id] = await fRes.json();
            } catch {}
          })
        );
        if (!cancelled) setFieldsByGame(next);
      } catch {}
    })();
    (async () => {
      try {
        const res = await fetch("/api/squads/mine", { credentials: "include" });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.squad) setSquad({ name: data.squad.name, members: data.squad.members || [] });
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Until the option definitions land, treat interests as unresolved rather
  // than missing: claiming "0 of 2 ready to raid" for a beat and then
  // correcting itself is worse than staying quiet.
  const fieldsLoaded = Object.keys(fieldsByGame).length > 0;
  const countRaidInterests = useCallback(
    (char: any) => {
      if (!fieldsLoaded) return 1;
      const groups = fieldsByGame[char.gameId ?? char.game_id] || [];
      const ids = new Set(
        groups.filter((g) => g.key === "raid_interest" || g.key === "raid_preferences").map((g) => g.id)
      );
      if (ids.size === 0) return 1;
      return (char.options || []).filter((o: any) => ids.has(o.group_id)).length;
    },
    [fieldsByGame, fieldsLoaded]
  );

  // The account's reset day, so the week labels below name the day the week
  // actually opened rather than the Wednesday storage label.
  const accountResetRule = useMemo(
    () => ruleForAccount(characters, Object.values(fieldsByGame).flat() as any[]),
    [characters, fieldsByGame]
  );

  const readiness = useMemo(
    () => computeReadiness({ accountAvailability, weeklyAvailability, characters, squad, countRaidInterests, resetRule: accountResetRule }),
    [accountAvailability, weeklyAvailability, characters, squad, countRaidInterests, accountResetRule]
  );

  const confirmWeek = useCallback(
    async (week: "current" | "next") => {
      if (confirming) return;
      setConfirming(week);
      const active = week === "current" ? weeklyAvailability.currentWeek : weeklyAvailability.nextWeek;
      const weekStart = active?.weekStart || (week === "current" ? getCurrentWednesday() : getNextWednesday());
      try {
        const res = await fetch("/me/availability/confirm", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ weekStart }),
        });
        if (!res.ok) return;
        const data = await res.json();
        const confirmedWeekStart = data.confirmedWeekStart || data.weekStart || weekStart;
        const end = new Date(confirmedWeekStart + "T00:00:00Z");
        end.setUTCDate(end.getUTCDate() + 6);
        const updated: Week = {
          weekEnd: end.toISOString().split("T")[0],
          slots: "",
          ...(active || {}),
          weekStart: confirmedWeekStart,
          confirmedAt: new Date().toISOString(),
          confirmedVia: "web",
        };
        setWeeklyAvailability((prev) =>
          week === "current" ? { ...prev, currentWeek: updated } : { ...prev, nextWeek: updated }
        );
      } catch {
        /* the row stays amber, which is the honest outcome */
      } finally {
        setConfirming(null);
      }
    },
    [confirming, weeklyAvailability, setWeeklyAvailability]
  );

  // One place decides what a row means when you click it, so the chips, the
  // dropdown and the call to action can never disagree.
  const runTarget = useCallback(
    (target?: ReadyTarget) => {
      if (!target) return;
      switch (target.kind) {
        case "availability":
          goToTabAndScroll("Roster", "availability");
          break;
        case "confirm-week":
          confirmWeek(target.week);
          break;
        case "raid-interests":
          if (target.charId) sessionStorage.setItem("openCharEditId", target.charId);
          goToTabAndScroll("Roster");
          break;
        case "guild":
          if (target.openGuildId) {
            sessionStorage.setItem("openGuildId", target.openGuildId);
            window.location.hash = "Guilds";
          } else {
            goToTabAndScroll("Roster", "join-guild");
          }
          break;
        case "join-guild":
          goToTabAndScroll("Roster", "join-guild");
          break;
        case "squad":
          goToTabAndScroll("Roster", "squad");
          break;
        case "bis":
          if (target.charId) sessionStorage.setItem("openCharBisId", target.charId);
          goToTabAndScroll("Roster");
          break;
      }
    },
    [confirmWeek]
  );

  // A player with no characters gets the onboarding tour instead: a checklist
  // reading "no toons" five times over is noise, not help. Anything nested
  // inside still renders, since the lockout rail is not ours to hide.
  if (!characters.length) {
    return children ? <div className="w-full">{children}</div> : null;
  }

  const { rows, syncLabel, cta, done, total } = readiness;
  const allDone = done === total;
  const blocking = rows.filter((r) => !r.optional && r.state === "attention");
  const ringColor = allDone ? "#34d399" : "#F59E0B";
  const circumference = 2 * Math.PI * 15.5;
  const offset = circumference * (1 - (total ? done / total : 0));

  const currentStart = weeklyAvailability.currentWeek?.weekStart;
  const nextStart = weeklyAvailability.nextWeek?.weekStart;
  const settledFor = [currentStart, nextStart]
    .filter(Boolean)
    .map((iso) => new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" }));

  const chip = "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold transition-all";
  // One constant for both rows: the ring above and the first lockout portrait
  // below have to start on the same vertical line, and two hand-matched
  // padding strings drift the moment one of them is edited. Vertical padding
  // stays per-row because the nested rail brings its own py-1.
  const rowPadX = "px-3 sm:px-4";

  return (
    <div
      ref={rootRef}
      data-tour="ready-bar"
      className={`rounded-2xl border bg-[var(--c-bg-veil)] transition-colors ${
        allDone ? "border-emerald-500/[0.18] hover:border-emerald-500/30" : "border-amber-500/20 hover:border-amber-500/35"
      }`}
    >
      <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5 ${rowPadX}`}>
      {/* Ring + status, standing where the guild bar puts its logo and name */}
      <div className="flex items-center gap-2.5 min-w-0 flex-shrink-0">
        <div className="relative h-[38px] w-[38px] shrink-0">
          <svg viewBox="0 0 36 36" className="h-[38px] w-[38px] -rotate-90" aria-hidden>
            <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.09)" strokeWidth="3" />
            <circle
              cx="18" cy="18" r="15.5" fill="none" stroke={ringColor} strokeWidth="3" strokeLinecap="round"
              strokeDasharray={circumference} strokeDashoffset={offset}
              className="transition-[stroke-dashoffset] duration-500 ease-out"
            />
          </svg>
          <div className="absolute inset-0 grid place-items-center text-[11px] font-semibold tabular-nums" style={{ color: ringColor }}>
            {done}/{total}
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-[13px] font-bold uppercase leading-tight tracking-[0.09em] text-[var(--c-ink)]">Raid Ready</div>
          <div className="mt-0.5 text-[11px] leading-snug text-[var(--c-ink-3)]">
            {allDone
              ? settledFor.length === 2
                ? `You're all set for ${settledFor[0]} and ${settledFor[1]}`
                : "You're all set"
              : `${done} of ${total} done`}
          </div>
        </div>
      </div>

      {/* Only the rows actually holding you back earn a chip */}
      {blocking.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {blocking.map((row) => (
            <button
              key={row.key}
              type="button"
              disabled={row.target?.kind === "confirm-week" && confirming === row.target.week}
              onClick={() => runTarget(row.target)}
              className={`${chip} border border-amber-500/30 bg-amber-500/[0.09] text-amber-800 dark:text-amber-200 hover:border-amber-500/50 hover:bg-amber-500/[0.16] disabled:opacity-50`}
            >
              <RowIcon state="attention" />
              <span>{row.label}</span>
              {row.actionLabel && (
                <span className="rounded border border-amber-500/45 px-1.5 py-[1px] text-[9px] uppercase tracking-[0.1em] text-amber-700 dark:text-amber-400">
                  {row.target?.kind === "confirm-week" && confirming === row.target.week ? "..." : row.actionLabel}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Right rail: the guild bar's divider plus either the nag or the calm state */}
      <div className="flex items-center gap-2 ml-auto flex-shrink-0">
        {allDone && (
          <span className="hidden sm:flex items-center gap-2 text-[11px] font-medium text-[var(--c-ink-3)]">
            <SyncIcon />
            <span>{syncLabel}</span>
          </span>
        )}

        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className={`${chip} border ${open ? "text-[var(--c-ink)] bg-[var(--c-sunken)] border-[var(--c-line-strong)]" : "text-[var(--c-ink-3)] border-transparent hover:text-[var(--c-ink)] hover:bg-[var(--c-sunken)]"}`}
          >
            <ChecklistIcon />
            <span>All checks</span>
            <span className={`transition-transform ${open ? "rotate-180" : ""}`}><ChevronIcon /></span>
          </button>
          {open && (
            <div className="absolute right-0 top-full mt-1 z-50 min-w-[260px] rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] shadow-xl shadow-black/60 p-1">
              {rows.map((row, i) => {
                const startsOptional = !!row.optional && !rows[i - 1]?.optional;
                const Tag = row.target ? "button" : "div";
                return (
                  <React.Fragment key={row.key}>
                    {startsOptional && (
                      <div className="flex items-center gap-2 px-2.5 py-1.5" aria-hidden>
                        <span className="text-[8px] font-semibold uppercase tracking-[0.18em] text-[var(--c-ink-4)]">Optional</span>
                        <span className="h-px flex-1 bg-[var(--c-sunken)]" />
                      </div>
                    )}
                    <Tag
                      {...(row.target ? { type: "button" as const, onClick: () => { setOpen(false); runTarget(row.target); } } : {})}
                      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left ${row.target ? "hover:bg-[var(--c-sunken)] transition-colors" : ""}`}
                    >
                      <RowIcon state={row.state} optional={row.optional} />
                      <span className={`flex-1 min-w-0 truncate text-[11.5px] ${
                        row.state === "attention" ? "text-amber-800 dark:text-amber-200" : row.optional ? "text-[var(--c-ink-3)]" : row.state === "done" ? "text-[var(--c-ink-2)]" : "text-[var(--c-ink-3)]"
                      }`}>
                        {row.label}
                      </span>
                      <span className={`shrink-0 text-[10.5px] ${row.optional ? "text-[var(--c-ink-4)]" : "text-[var(--c-ink-3)]"}`}>{row.value}</span>
                    </Tag>
                  </React.Fragment>
                );
              })}
              <div className="flex items-center gap-2 border-t border-[var(--c-line-soft)] mt-1 px-2.5 py-2 text-[10.5px] text-[var(--c-ink-3)]">
                <SyncIcon />
                <span className="truncate">{syncLabel}</span>
              </div>
            </div>
          )}
        </div>

        {cta && !allDone && (
          <>
            <span className="hidden sm:inline-block w-px h-5 bg-[var(--c-sunken-2)]" />
            <button
              type="button"
              disabled={cta.target.kind === "confirm-week" && confirming === cta.target.week}
              onClick={() => runTarget(cta.target)}
              className="flex items-center gap-1.5 rounded-lg border border-amber-500/35 bg-amber-500/[0.14] px-3.5 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-amber-800 dark:text-amber-200 transition-colors hover:border-amber-500/55 hover:bg-amber-500/[0.22] hover:text-amber-800 dark:hover:text-amber-100 disabled:opacity-50"
            >
              <span>{cta.target.kind === "confirm-week" && confirming === cta.target.week ? "Confirming..." : cta.label}</span>
              <ArrowIcon />
            </button>
          </>
        )}
      </div>
      </div>

      {/* The weekly lockout rail rides inside the same shell, so the bar reads
          as its header rather than a third strip stacked on the home page.
          py-1.5 rather than the header's py-2.5: the rail adds its own py-1 for
          focus rings, so this lands on the same 10px the header uses. */}
      {children && (
        <div className={`border-t border-[var(--c-line-soft)] py-1.5 ${rowPadX}`}>{children}</div>
      )}
    </div>
  );
}
